<template>
  <div
    ref="item"
    class="vue-grid-item"
    :class="classObj"
    :style="style"
  >
    <slot></slot>
    <span
      v-if="resizableAndNotStatic"
      ref="handle"
      :class="resizableHandleClass"
    ></span>
    <!--<span v-if="draggable" ref="dragHandle" class="vue-draggable-handle"></span>-->
  </div>
</template>
<script setup lang="ts">
import interact from 'interactjs';
import { computed, getCurrentInstance, inject, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { getDocumentDir } from '../helpers/dom';
import { createCoreData, getControlPosition } from '../helpers/draggable-utils';
import { setTopLeft, setTopRight, setTransform, setTransformRtl } from '../helpers/utils';
import type { Layout, LayoutItem } from '../helpers/utils';
defineOptions({
  name: 'GridItem',
});

const props = withDefaults(defineProps<LayoutItem & { usePercentages?: boolean }>(), {
  isDraggable: null,
  isResizable: null,
  static: false,
  minH: 1,
  minW: 1,
  maxH: Infinity,
  maxW: Infinity,
  dragIgnoreFrom: 'a, button',
  dragAllowFrom: null,
  resizeIgnoreFrom: 'a, button',
  usePercentages: null,
});

const emit = defineEmits<{
  (
    e: 'container-resized',
    i: string | number,
    h: number | string,
    w: number | string,
    height: string,
    width: string,
  ): void;
  (
    e: 'resize',
    i: string | number,
    h: number | string,
    w: number | string,
    height: number,
    width: number,
  ): void;
  (
    e: 'resized',
    i: string | number,
    h: number | string,
    w: number | string,
    height: number,
    width: number,
  ): void;
  (e: 'move', i: string | number, x: number | string, y: number | string): void;
  (e: 'moved', i: string | number, x: number | string, y: number | string): void;
}>();

const eventBus = inject<any>('eventBus');
const injectedUsePercentages = inject<boolean>('usePercentages', false);
const layout = inject<Layout>('layout');

const item = ref<HTMLElement | null>(null);
const handle = ref<HTMLElement | null>(null);

const cols = ref(1);
const containerWidth = ref(100);
const containerHeight = ref(100);
const rowHeight = ref(10);
const margin = ref([10, 10]);
const maxRows = ref(Infinity);
const draggable = ref<boolean | null>(null);
const resizable = ref<boolean | null>(null);
const useCssTransforms = ref(true);
const isDragging = ref(false);
const dragging = ref<any>(null);
const isResizing = ref(false);
const resizing = ref<any>(null);
const lastX = ref(NaN);
const lastY = ref(NaN);
const lastW = ref(NaN);
const lastH = ref(NaN);
const style = ref<Record<string, any>>({});
const rtl = ref(false);
const dragEventSet = ref(false);
const resizeEventSet = ref(false);
const previousW = ref<number | string | null>(null);
const previousH = ref<number | string | null>(null);
const previousX = ref<number | string | null>(null);
const previousY = ref<number | string | null>(null);
const innerX = ref<number | string>(props.x);
const innerY = ref<number | string>(props.y);
const innerW = ref<number | string>(props.w);
const innerH = ref<number | string>(props.h);
const interactObj = ref<any>(null);

const getVal = (v: number | string): number => (typeof v === 'number' ? v : parseFloat(v) || 0);

const parseUnit = (val: string | number | undefined, pos?: string | number): string => {
  if (val === undefined || val === null) return '0%';
  if (typeof val === 'number') return val + '%';
  if (val.endsWith('%') || val.endsWith('px') || val.includes('calc')) return val;
  if (val === 'grow') {
    if (pos !== undefined) {
      const p = typeof pos === 'number' ? pos + '%' : pos;
      return `calc(100% - ${p})`;
    }
    return '100%';
  }
  return val.includes('%') || val.includes('px') || val.includes('calc') ? val : val + '%';
};

const usePercentagesInternal = computed(() => {
  return props.usePercentages !== null ? props.usePercentages : injectedUsePercentages;
});

const resizableAndNotStatic = computed(() => {
  return resizable.value && !props.static;
});

const draggableOrResizableAndNotStatic = computed(() => {
  return (draggable.value || resizable.value) && !props.static;
});

const isAndroid = computed(() => {
  return navigator.userAgent.toLowerCase().indexOf('android') !== -1;
});

const renderRtl = computed(() => {
  const instance = getCurrentInstance();
  const parent = instance?.parent?.proxy as any;
  return parent?.isMirrored ? !rtl.value : rtl.value;
});

const classObj = computed(() => {
  return {
    'vue-resizable': resizableAndNotStatic.value,
    static: props.static,
    resizing: isResizing.value,
    'vue-draggable-dragging': isDragging.value,
    cssTransforms: useCssTransforms.value,
    'render-rtl': renderRtl.value,
    'disable-userselect': isDragging.value,
    'no-touch': isAndroid.value && draggableOrResizableAndNotStatic.value,
  };
});

const resizableHandleClass = computed(() => {
  if (renderRtl.value) {
    return 'vue-resizable-handle vue-rtl-resizable-handle';
  }
  return 'vue-resizable-handle';
});

const calcColWidth = () => {
  return (containerWidth.value - (margin.value[0] || 10) * (cols.value + 1)) / cols.value;
};

const calcPosition = (
  x: number | string,
  y: number | string,
  w: number | string,
  h: number | string,
) => {
  if (usePercentagesInternal.value) {
    const out: any = {
      width: parseUnit(w, x),
      height: parseUnit(h, y),
      top: parseUnit(y),
    };
    if (renderRtl.value) {
      out.right = parseUnit(x);
    } else {
      out.left = parseUnit(x);
    }
    return out;
  }

  const colWidth = calcColWidth();
  let out: any = null;
  const nx = getVal(x);
  const ny = getVal(y);
  const nw = getVal(w);
  const nh = getVal(h);

  if (renderRtl.value) {
    out = {
      right:
        typeof x === 'string' && (x.includes('calc') || x.includes('px'))
          ? x
          : Math.round(colWidth * nx + (nx + 1) * margin.value[0]),
      top:
        typeof y === 'string' && (y.includes('calc') || y.includes('px'))
          ? y
          : Math.round(rowHeight.value * ny + (ny + 1) * margin.value[1]),
      width:
        typeof w === 'string' && (w.includes('calc') || w.includes('px'))
          ? w
          : nw === Infinity
          ? nw
          : Math.round(colWidth * nw + Math.max(0, nw - 1) * margin.value[0]),
      height:
        typeof h === 'string' && (h.includes('calc') || h.includes('px'))
          ? h
          : nh === Infinity
          ? nh
          : Math.round(rowHeight.value * nh + Math.max(0, nh - 1) * margin.value[1]),
    };
  } else {
    out = {
      left:
        typeof x === 'string' && (x.includes('calc') || x.includes('px'))
          ? x
          : Math.round(colWidth * nx + (nx + 1) * margin.value[0]),
      top:
        typeof y === 'string' && (y.includes('calc') || y.includes('px'))
          ? y
          : Math.round(rowHeight.value * ny + (ny + 1) * margin.value[1]),
      width:
        typeof w === 'string' && (w.includes('calc') || w.includes('px'))
          ? w
          : nw === Infinity
          ? nw
          : Math.round(colWidth * nw + Math.max(0, nw - 1) * margin.value[0]),
      height:
        typeof h === 'string' && (h.includes('calc') || h.includes('px'))
          ? h
          : nh === Infinity
          ? nh
          : Math.round(rowHeight.value * nh + Math.max(0, nh - 1) * margin.value[1]),
    };
  }
  return out;
};

const resolveItemValue = (id: string, prop: 'x' | 'y'): string | number => {
  if (!layout) return 0;
  const item = (layout as any).find((l: any) => l.i === id);
  if (!item) return 0;

  if (prop === 'y') {
    if (item.top_i !== undefined && item.top_i !== null) {
      const refItem = (layout as any).find((l: any) => l.i === item.top_i);
      const refY = resolveItemValue(item.top_i, 'y');
      return `calc(${parseUnit(refY)} + ${parseUnit(refItem?.h ?? 0)})`;
    }
    return item.y ?? 0;
  } else {
    if (item.left_i !== undefined && item.left_i !== null) {
      const refItem = (layout as any).find((l: any) => l.i === item.left_i);
      const refX = resolveItemValue(item.left_i, 'x');
      return `calc(${parseUnit(refX)} + ${parseUnit(refItem?.w ?? 0)})`;
    }
    return item.x ?? 0;
  }
};

const createStyle = () => {
  if (!usePercentagesInternal.value) {
    const px = getVal(props.x);
    const pw = getVal(props.w);
    if (px + pw > cols.value) {
      innerX.value = 0;
      innerW.value = pw > cols.value ? cols.value : pw;
    } else {
      innerX.value = props.x;
      innerW.value = props.w;
    }
  } else {
    innerX.value = props.x;
    innerW.value = props.w;
  }

  const actualX = resolveItemValue(String(props.i), 'x');
  const actualY = resolveItemValue(String(props.i), 'y');

  const pos = calcPosition(actualX, actualY, innerW.value, innerH.value);
  if (isDragging.value) {
    pos.top = dragging.value.top;
    if (typeof pos.top === 'number') pos.top += 'px';
    if (renderRtl.value) {
      pos.right = dragging.value.left;
      if (typeof pos.right === 'number') pos.right += 'px';
    } else {
      pos.left = dragging.value.left;
      if (typeof pos.left === 'number') pos.left += 'px';
    }
  }
  if (isResizing.value) {
    pos.width = resizing.value.width;
    if (typeof pos.width === 'number') pos.width += 'px';
    pos.height = resizing.value.height;
    if (typeof pos.height === 'number') pos.height += 'px';
  }
  let s: any = null;

  if (useCssTransforms.value && (isDragging.value || isResizing.value)) {
    const getPx = (v: any, containerSize: number) => {
      if (typeof v === 'number') return v;
      if (typeof v !== 'string') return 0;
      if (v.endsWith('%')) return (parseFloat(v) / 100) * containerSize;
      if (v.endsWith('px')) return parseFloat(v);
      if (v.includes('calc')) {
        // Simple heuristic for calc(100% - pos)
        if (v.includes('100%')) {
          const parts = v.split('-');
          if (parts.length > 1) {
            return containerSize - getPx(parts[1].trim().replace(')', ''), containerSize);
          }
        }
      }
      return parseFloat(v) || 0;
    };

    const topPx = getPx(pos.top, containerHeight.value);
    const widthPx = getPx(pos.width, containerWidth.value);
    const heightPx = getPx(pos.height, containerHeight.value);

    if (renderRtl.value) {
      const rightPx = getPx(pos.right, containerWidth.value);
      s = setTransformRtl(topPx, rightPx, widthPx, heightPx);
    } else {
      const leftPx = getPx(pos.left, containerWidth.value);
      s = setTransform(topPx, leftPx, widthPx, heightPx);
    }
  } else if (useCssTransforms.value && !usePercentagesInternal.value) {
    if (renderRtl.value) {
      s = setTransformRtl(pos.top, pos.right!, pos.width, pos.height);
    } else {
      s = setTransform(pos.top, pos.left!, pos.width, pos.height);
    }
  } else {
    if (renderRtl.value) {
      s = setTopRight(pos.top, pos.right!, pos.width, pos.height);
    } else {
      s = setTopLeft(pos.top, pos.left!, pos.width, pos.height);
    }
  }

  for (const key of ['top', 'left', 'right', 'width', 'height']) {
    if (s[key] !== undefined && typeof s[key] === 'number') {
      s[key] += 'px';
    }
  }

  style.value = s;
};

const emitContainerResized = () => {
  const styleProps: any = {};
  for (const prop of ['width', 'height']) {
    let val = style.value[prop];
    if (typeof val === 'string' && (val.includes('calc') || val.includes('%'))) {
      if (item.value) {
        val = prop === 'width' ? item.value.offsetWidth + 'px' : item.value.offsetHeight + 'px';
      } else {
        return;
      }
    }
    const matches = String(val).match(/^(\d+(\.\d+)?)px$/);
    if (!matches) return;
    styleProps[prop] = matches[1];
  }
  emit('container-resized', props.i, props.h, props.w, styleProps.height, styleProps.width);
};

const calcXY = (top: number, left: number) => {
  if (usePercentagesInternal.value) {
    let x = (left / containerWidth.value) * 100;
    let y = (top / containerHeight.value) * 100;

    x = Math.max(Math.min(x, 100 - getVal(innerW.value)), 0);
    y = Math.max(Math.min(y, 100 - getVal(innerH.value)), 0);
    return { x, y };
  }

  const colWidth = calcColWidth();
  let x = Math.round((left - margin.value[0]) / (colWidth + margin.value[0]));
  let y = Math.round((top - margin.value[1]) / (rowHeight.value + margin.value[1]));
  x = Math.max(Math.min(x, cols.value - getVal(innerW.value)), 0);
  y = Math.max(Math.min(y, maxRows.value - getVal(innerH.value)), 0);
  return { x, y };
};

const calcWH = (height: number, width: number, autoSizeFlag = false) => {
  if (usePercentagesInternal.value) {
    let w = (width / containerWidth.value) * 100;
    let h = (height / containerHeight.value) * 100;

    w = Math.max(Math.min(w, 100 - getVal(innerX.value)), 0);
    h = Math.max(Math.min(h, 100 - getVal(innerY.value)), 0);
    return { w, h };
  }

  const colWidth = calcColWidth();
  let w = Math.round((width + margin.value[0]) / (colWidth + margin.value[0]));
  let h = 0;
  if (!autoSizeFlag) {
    h = Math.round((height + margin.value[1]) / (rowHeight.value + margin.value[1]));
  } else {
    h = Math.ceil((height + margin.value[1]) / (rowHeight.value + margin.value[1]));
  }
  w = Math.max(Math.min(w, cols.value - getVal(innerX.value)), 0);
  h = Math.max(Math.min(h, maxRows.value - getVal(innerY.value)), 0);
  return { w, h };
};

const handleDrag = (event: any) => {
  if (props.static) return;
  if (isResizing.value) return;
  const position = getControlPosition(event);
  if (position === null) return;
  const { x, y } = position;
  const newPosition = { top: 0, left: 0 };
  switch (event.type) {
    case 'dragstart': {
      previousX.value = innerX.value;
      previousY.value = innerY.value;
      const parentRect = event.target.offsetParent.getBoundingClientRect();
      const clientRect = event.target.getBoundingClientRect();
      if (renderRtl.value) {
        newPosition.left = (clientRect.right - parentRect.right) * -1;
      } else {
        newPosition.left = clientRect.left - parentRect.left;
      }
      newPosition.top = clientRect.top - parentRect.top;
      dragging.value = newPosition;
      isDragging.value = true;
      break;
    }
    case 'dragend': {
      if (!isDragging.value) return;
      const parentRect = event.target.offsetParent.getBoundingClientRect();
      const clientRect = event.target.getBoundingClientRect();
      if (renderRtl.value) {
        newPosition.left = (clientRect.right - parentRect.right) * -1;
      } else {
        newPosition.left = clientRect.left - parentRect.left;
      }
      newPosition.top = clientRect.top - parentRect.top;
      dragging.value = null;
      isDragging.value = false;
      break;
    }
    case 'dragmove': {
      const coreEvent = createCoreData(lastX.value, lastY.value, x, y);
      if (renderRtl.value) {
        newPosition.left = dragging.value.left - coreEvent.deltaX;
      } else {
        newPosition.left = dragging.value.left + coreEvent.deltaX;
      }
      newPosition.top = dragging.value.top + coreEvent.deltaY;
      dragging.value = newPosition;
      break;
    }
  }
  let pos = calcXY(newPosition.top, newPosition.left);
  lastX.value = x;
  lastY.value = y;
  if (getVal(innerX.value) !== pos.x || getVal(innerY.value) !== pos.y) {
    emit('move', props.i, pos.x, pos.y);
  }
  if (
    event.type === 'dragend' &&
    (getVal(previousX.value!) !== pos.x || getVal(previousY.value!) !== pos.y)
  ) {
    emit('moved', props.i, pos.x, pos.y);
  }
  if (usePercentagesInternal.value) {
    eventBus.emit('dragEvent', {
      eventType: event.type,
      i: props.i,
      x: pos.x,
      y: pos.y,
      h: innerH.value,
      w: innerW.value,
    });
  } else {
    eventBus.emit('dragEvent', {
      eventType: event.type,
      i: props.i,
      x: pos.x,
      y: pos.y,
      h: getVal(innerH.value),
      w: getVal(innerW.value),
    });
  }
};

const handleResize = (event: any) => {
  if (props.static) return;
  const position = getControlPosition(event);
  if (position == null) return;
  const { x, y } = position;
  const newSize = { width: 0, height: 0 };
  let posStyle: any = null;
  switch (event.type) {
    case 'resizestart': {
      previousW.value = innerW.value;
      previousH.value = innerH.value;
      posStyle = calcPosition(innerX.value, innerY.value, innerW.value, innerH.value);

      const getPx = (v: any, containerSize: number) => {
        if (typeof v === 'number') return v;
        if (typeof v !== 'string') return 0;
        if (v.endsWith('%')) return (parseFloat(v) / 100) * containerSize;
        if (v.endsWith('px')) return parseFloat(v);
        return parseFloat(v) || 0;
      };

      newSize.width = getPx(posStyle.width, containerWidth.value);
      newSize.height = getPx(posStyle.height, containerHeight.value);

      resizing.value = newSize;
      isResizing.value = true;
      break;
    }
    case 'resizemove': {
      const coreEvent = createCoreData(lastW.value, lastH.value, x, y);
      if (renderRtl.value) {
        newSize.width = resizing.value.width - coreEvent.deltaX;
      } else {
        newSize.width = resizing.value.width + coreEvent.deltaX;
      }
      newSize.height = resizing.value.height + coreEvent.deltaY;
      resizing.value = newSize;
      break;
    }
    case 'resizeend': {
      posStyle = calcPosition(innerX.value, innerY.value, innerW.value, innerH.value);

      const getPx = (v: any, containerSize: number) => {
        if (typeof v === 'number') return v;
        if (typeof v !== 'string') return 0;
        if (v.endsWith('%')) return (parseFloat(v) / 100) * containerSize;
        if (v.endsWith('px')) return parseFloat(v);
        return parseFloat(v) || 0;
      };

      newSize.width = getPx(posStyle.width, containerWidth.value);
      newSize.height = getPx(posStyle.height, containerHeight.value);

      resizing.value = null;
      isResizing.value = false;
      break;
    }
  }
  const pos = calcWH(newSize.height, newSize.width);
  if (!usePercentagesInternal.value) {
    if (pos.w < props.minW) pos.w = props.minW;
    if (pos.w > props.maxW) pos.w = props.maxW;
    if (pos.h < props.minH) pos.h = props.minH;
    if (pos.h > props.maxH) pos.h = props.maxH;
    if (pos.h < 1) pos.h = 1;
    if (pos.w < 1) pos.w = 1;
  }
  lastW.value = x;
  lastH.value = y;
  if (getVal(innerW.value) !== pos.w || getVal(innerH.value) !== pos.h) {
    emit('resize', props.i, pos.h, pos.w, newSize.height, newSize.width);
  }
  if (
    event.type === 'resizeend' &&
    (getVal(previousW.value!) !== pos.w || getVal(previousH.value!) !== pos.h)
  ) {
    emit('resized', props.i, pos.h, pos.w, newSize.height, newSize.width);
  }
  eventBus.emit('resizeEvent', {
    eventType: event.type,
    i: props.i,
    x: getVal(innerX.value),
    y: getVal(innerY.value),
    h: pos.h,
    w: pos.w,
  });
};

const tryMakeDraggable = () => {
  if (interactObj.value === null || interactObj.value === undefined) {
    interactObj.value = interact(item.value as any);
  }
  if (draggable.value && !props.static) {
    const opts = {
      ignoreFrom: props.dragIgnoreFrom,
      allowFrom: props.dragAllowFrom,
    };
    interactObj.value.draggable(opts);
    if (!dragEventSet.value) {
      dragEventSet.value = true;
      interactObj.value.on('dragstart dragmove dragend', (event: any) => {
        handleDrag(event);
      });
    }
  } else {
    interactObj.value.draggable({
      enabled: false,
    });
  }
};

const tryMakeResizable = () => {
  if (interactObj.value === null || interactObj.value === undefined) {
    interactObj.value = interact(item.value as any);
  }
  if (resizable.value && !props.static) {
    let maximumPx: any;
    let minimumPx: any;

    if (usePercentagesInternal.value) {
      maximumPx = {
        width: (props.maxW / 100) * containerWidth.value,
        height: (props.maxH / 100) * containerHeight.value,
      };
      minimumPx = {
        width: (props.minW / 100) * containerWidth.value,
        height: (props.minH / 100) * containerHeight.value,
      };
    } else {
      const maximum = calcPosition(0, 0, props.maxW, props.maxH);
      const minimum = calcPosition(0, 0, props.minW, props.minH);
      maximumPx = {
        width: parseFloat(maximum.width),
        height: parseFloat(maximum.height),
      };
      minimumPx = {
        width: parseFloat(minimum.width),
        height: parseFloat(minimum.height),
      };
    }

    const opts = {
      preserveAspectRatio: true,
      edges: {
        left: false,
        right: '.' + resizableHandleClass.value,
        bottom: '.' + resizableHandleClass.value,
        top: false,
      },
      ignoreFrom: props.resizeIgnoreFrom,
      restrictSize: {
        min: minimumPx,
        max: maximumPx,
      },
    };
    interactObj.value.resizable(opts);
    if (!resizeEventSet.value) {
      resizeEventSet.value = true;
      interactObj.value.on('resizestart resizemove resizeend', (event: any) => {
        handleResize(event);
      });
    }
  } else {
    interactObj.value.resizable({
      enabled: false,
    });
  }
};

const updateWidth = (width: number, colNum?: number) => {
  containerWidth.value = width || 100;
  if (colNum !== undefined && colNum !== null) {
    cols.value = colNum;
  }
  if (item.value && item.value.parentElement) {
    containerHeight.value = item.value.parentElement.offsetHeight || 100;
  }
};

const compact = () => {
  createStyle();
};

const autoSize = () => {
  previousW.value = innerW.value;
  previousH.value = innerH.value;
  if (item.value && item.value.firstElementChild) {
    const newSize = item.value.firstElementChild.getBoundingClientRect();
    const pos = calcWH(newSize.height, newSize.width, true);
    if (!usePercentagesInternal.value) {
      if (pos.w < props.minW) pos.w = props.minW;
      if (pos.w > props.maxW) pos.w = props.maxW;
      if (pos.h < props.minH) pos.h = props.minH;
      if (pos.h > props.maxH) pos.h = props.maxH;
      if (pos.h < 1) pos.h = 1;
      if (pos.w < 1) pos.w = 1;
    }
    if (getVal(innerW.value) !== pos.w || getVal(innerH.value) !== pos.h) {
      emit('resize', props.i, pos.h, pos.w, newSize.height, newSize.width);
    }
    if (getVal(previousW.value!) !== pos.w || getVal(previousH.value!) !== pos.h) {
      emit('resized', props.i, pos.h, pos.w, newSize.height, newSize.width);
      eventBus.emit('resizeEvent', {
        eventType: 'resizeend',
        i: props.i,
        x: getVal(innerX.value),
        y: getVal(innerY.value),
        h: pos.h,
        w: pos.w,
      });
    }
  }
};

const updateWidthHandler = (width: number) => {
  updateWidth(width);
};
const compactHandler = () => {
  compact();
};
const setDraggableHandler = (isDraggable: boolean) => {
  if (props.isDraggable === null) {
    draggable.value = isDraggable;
  }
};
const setResizableHandler = (isResizable: boolean) => {
  if (props.isResizable === null) {
    resizable.value = isResizable;
  }
};
const setRowHeightHandler = (val: number) => {
  rowHeight.value = val;
};
const setMaxRowsHandler = (val: number) => {
  maxRows.value = val;
};
const directionchangeHandler = () => {
  rtl.value = getDocumentDir() === 'rtl';
  compact();
};
const setColNum = (colNum: any) => {
  cols.value = parseInt(colNum, 10);
};

onMounted(() => {
  const instance = getCurrentInstance();
  const parent = instance?.parent?.proxy as any;

  cols.value = parent.colNum;
  rowHeight.value = parent.rowHeight;
  containerWidth.value = parent.width !== null && parent.width !== 0 ? parent.width : 100;
  margin.value = parent.margin !== undefined ? parent.margin : [10, 10];
  maxRows.value = parent.maxRows;

  if (item.value && item.value.parentElement) {
    containerHeight.value = item.value.parentElement.offsetHeight || 100;
    if (containerWidth.value === 100) {
      containerWidth.value = item.value.parentElement.offsetWidth || 100;
    }
  }

  if (props.isDraggable === null) {
    draggable.value = parent.isDraggable;
  } else {
    draggable.value = props.isDraggable;
  }
  if (props.isResizable === null) {
    resizable.value = parent.isResizable;
  } else {
    resizable.value = props.isResizable;
  }
  useCssTransforms.value = parent.useCssTransforms;

  eventBus.on('updateWidth', updateWidthHandler);
  eventBus.on('compact', compactHandler);
  eventBus.on('setDraggable', setDraggableHandler);
  eventBus.on('setResizable', setResizableHandler);
  eventBus.on('setRowHeight', setRowHeightHandler);
  eventBus.on('setMaxRows', setMaxRowsHandler);
  eventBus.on('directionchange', directionchangeHandler);
  eventBus.on('setColNum', setColNum);
  rtl.value = getDocumentDir() === 'rtl';

  createStyle();
});

onBeforeUnmount(() => {
  eventBus.off('updateWidth', updateWidthHandler);
  eventBus.off('compact', compactHandler);
  eventBus.off('setDraggable', setDraggableHandler);
  eventBus.off('setResizable', setResizableHandler);
  eventBus.off('setRowHeight', setRowHeightHandler);
  eventBus.off('setMaxRows', setMaxRowsHandler);
  eventBus.off('directionchange', directionchangeHandler);
  eventBus.off('setColNum', setColNum);
  if (interactObj.value) {
    interactObj.value.unset();
  }
});

watch(
  () => props.isDraggable,
  () => {
    draggable.value = props.isDraggable;
  },
);

watch(
  () => props.static,
  () => {
    tryMakeDraggable();
    tryMakeResizable();
  },
);

watch(draggable, () => {
  tryMakeDraggable();
});

watch(
  () => props.isResizable,
  () => {
    resizable.value = props.isResizable;
  },
);

watch(resizable, () => {
  tryMakeResizable();
});

watch(rowHeight, () => {
  createStyle();
  emitContainerResized();
});

watch(cols, () => {
  tryMakeResizable();
  createStyle();
  emitContainerResized();
});

watch(containerWidth, () => {
  tryMakeResizable();
  createStyle();
  emitContainerResized();
});

watch(containerHeight, () => {
  tryMakeResizable();
  createStyle();
  emitContainerResized();
});

watch(
  () => props.x,
  newVal => {
    innerX.value = newVal;
    createStyle();
  },
);

watch(
  () => props.y,
  newVal => {
    innerY.value = newVal;
    createStyle();
  },
);

watch(
  () => props.h,
  newVal => {
    innerH.value = newVal;
    createStyle();
  },
);

watch(
  () => props.w,
  newVal => {
    innerW.value = newVal;
    createStyle();
  },
);

watch(renderRtl, () => {
  tryMakeResizable();
  createStyle();
});

watch(
  () => props.minH,
  () => {
    tryMakeResizable();
  },
);

watch(
  () => props.maxH,
  () => {
    tryMakeResizable();
  },
);

watch(
  () => props.minW,
  () => {
    tryMakeResizable();
  },
);

watch(
  () => props.maxW,
  () => {
    tryMakeResizable();
  },
);

watch(
  () => {
    const instance = getCurrentInstance();
    const parent = instance?.parent?.proxy as any;
    return parent?.margin;
  },
  val => {
    if (!val || (val[0] === margin.value[0] && val[1] === margin.value[1])) {
      return;
    }
    margin.value = val.map((m: any) => Number(m));
    createStyle();
    emitContainerResized();
  },
);

defineExpose({
  autoSize,
  compact,
  updateWidth,
});
</script>

<style>
.vue-grid-item {
  transition: none;
  transition-property: left, top, right;
  /* add right for rtl */
}
.vue-grid-item.no-touch {
  -ms-touch-action: none;
  touch-action: none;
}

.vue-grid-item.cssTransforms {
  right: auto;
  left: 0;
  transition-property: transform;
}

.vue-grid-item.cssTransforms.render-rtl {
  right: 0;
  left: auto;
}

.vue-grid-item.resizing {
  z-index: 3;
  opacity: 0.6;
}

.vue-grid-item.vue-draggable-dragging {
  z-index: 3;
  transition: none;
}

.vue-grid-item.vue-grid-placeholder {
  z-index: 2;
  background: pink;
  transition-duration: 100ms;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  -o-user-select: none;
  user-select: none;
}

.vue-grid-item > .vue-resizable-handle {
  position: absolute;
  right: 0;
  bottom: 0;
  box-sizing: border-box;
  width: 20px;
  height: 20px;
  padding: 0 3px 3px 0;
  background: url('data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBzdGFuZGFsb25lPSJubyI/Pg08IS0tIEdlbmVyYXRvcjogQWRvYmUgRmlyZXdvcmtzIENTNiwgRXhwb3J0IFNWRyBFeHRlbnNpb24gYnkgQWFyb24gQmVhbGwgKGh0dHA6Ly9maXJld29ya3MuYWJlYWxsLmNvbSkgLiBWZXJzaW9uOiAwLjYuMSAgLS0+DTwhRE9DVFlQRSBzdmcgUFVCTElDICItLy9XM0MvL0RURCBTVkcgMS4xLy9FTiIgImh0dHA6Ly93d3cudzMub3JnL0dyYXBoaWNzL1NWRy8xLjEvRFREL3N2ZzExLmR0ZCI+DTxzdmcgaWQ9IlVudGl0bGVkLVBhZ2UlMjAxIiB2aWV3Qm94PSIwIDAgNiA2IiBzdHlsZT0iYmFja2dyb3VuZC1jb2xvcjojZmZmZmZmMDAiIHZlcnNpb249IjEuMSINCXhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgeG1sbnM6eGxpbms9Imh0dHA6Ly93d3cudzMub3JnLzE5OTkveGxpbmsiIHhtbDpzcGFjZT0icHJlc2VydmUiDQl4PSIwcHgiIHk9IjBweCIgd2lkdGg9IjZweCIgaGVpZ2h0PSI2cHgiDT4NCTxnIG9wYWNpdHk9IjAuMzAyIj4NCQk8cGF0aCBkPSJNIDYgNiBMIDAgNiBMIDAgNC4yIEwgNCA0LjIgTCA0LjIgNC4yIEwgNC4yIDAgTCA2IDAgTCA2IDYgTCA2IDYgWiIgZmlsbD0iIzAwMDAwMCIvPg0JPC9nPg08L3N2Zz4=');
  background-repeat: no-repeat;
  background-position: bottom right;
  background-origin: content-box;
  cursor: se-resize;
}

.vue-grid-item > .vue-rtl-resizable-handle {
  right: auto;
  bottom: 0;
  left: 0;
  padding-left: 3px;
  background: url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAuMDAwMDAwMDAwMDAwMDAyIiBoZWlnaHQ9IjEwLjAwMDAwMDAwMDAwMDAwMiIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KIDwhLS0gQ3JlYXRlZCB3aXRoIE1ldGhvZCBEcmF3IC0gaHR0cDovL2dpdGh1Yi5jb20vZHVvcGl4ZWwvTWV0aG9kLURyYXcvIC0tPgogPGc+CiAgPHRpdGxlPmJhY2tncm91bmQ8L3RpdGxlPgogIDxyZWN0IGZpbGw9Im5vbmUiIGlkPSJjYW52YXNfYmFja2dyb3VuZCIgaGVpZ2h0PSIxMiIgd2lkdGg9IjEyIiB5PSItMSIgeD0iLTEiLz4KICA8ZyBkaXNwbGF5PSJub25lIiBvdmVyZmxvdz0idmlzaWJsZSIgeT0iMCIgeD0iMCIgaGVpZ2h0PSIxMDAlIiB3aWR0aD0iMTAwJSIgaWQ9ImNhbnZhc0dyaWQiPgogICA8cmVjdCBmaWxsPSJ1cmwoI2dyaWRwYXR0ZXJuKSIgc3Ryb2tlLXdpZHRoPSIwIiB5PSIwIiB4PSIwIiBoZWlnaHQ9IjEwMCUiIHdpZHRoPSIxMDAlIi8+CiAgPC9nPgogPC9nPgogPGc+CiAgPHRpdGxlPkxheWVyIDE8L3RpdGxlPgogIDxsaW5lIGNhbnZhcz0iI2ZmZmZmZiIgY2FudmFzLW9wYWNpdHk9IjEiIHN0cm9rZS1saW5lY2FwPSJ1bmRlZmluZWQiIHN0cm9rZS1saW5lam9pbj0idW5kZWZpbmVkIiBpZD0ic3ZnXzEiIHkyPSItNzAuMTc4NDA3IiB4Mj0iMTI0LjQ2NDA3NSIgeTE9Ii0zOC4zOTI3MzciIHgxPSIxNDQuODIxMjg5IiBzdHJva2Utd2lkdGg9IjEuNSIgc3Ryb2tlPSIjMDAwIiBmaWxsPSJub25lIi8+CiAgPGxpbmUgc3Ryb2tlPSIjNjY2NjY2IiBzdHJva2UtbGluZWNhcD0idW5kZWZpbmVkIiBzdHJva2UtbGluZWpvaW49InVuZGVmaW5lZCIgaWQ9InN2Z181IiB5Mj0iOS4xMDY5NTciIHgyPSIwLjk0NzI0NyIgeTE9Ii0wLjAxODEyOCIgeDE9IjAuOTQ3MjQ3IiBzdHJva2Utd2lkdGg9IjIiIGZpbGw9Im5vbmUiLz4KICA8bGluZSBzdHJva2UtbGluZWNhcD0idW5kZWZpbmVkIiBzdHJva2UtbGluZWpvaW49InVuZGVmaW5lZCIgaWQ9InN2Z183IiB5Mj0iOSIgeDI9IjEwLjA3MzUyOSIgeTE9IjkiIHgxPSItMC42NTU2NCIgc3Ryb2tlLXdpZHRoPSIwIiBzdHJva2U9IiM2NjY2NjYiIGZpbGw9Im5vbmUiLz4KIDwvZz4KPC9zdmc+);
  background-repeat: no-repeat;
  background-position: bottom left;
  background-origin: content-box;
  cursor: sw-resize;
}

.vue-grid-item.disable-userselect {
  user-select: none;
}
</style>
